import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "475",
  "slug": "how-to-use-a-compass-find-north",
  "title": "How to Use a Compass: A Beginner's Guide to Finding North Without GPS",
  "excerpt": "A compass and a map will get you home when your phone dies. Here is how a compass actually works, why magnetic north is not true north, and how to take and follow a bearing.",
  "date": "2026-06-18",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1600706843784-6f0ad251f52f?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxjb21wYXNzJTIwaGlraW5nfGVufDF8MHx8fDE3ODE3NzA1MDd8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "content": [
    {
      "type": "paragraph",
      "text": "GPS is wonderful right up until the battery dies, the signal drops in a deep valley, or the screen cracks on a rock. A magnetic compass needs no power, no signal, and no software updates, and it has guided travellers for the better part of a thousand years. Learning to use one is one of the most satisfying and genuinely useful map skills you can pick up."
    },
    {
      "type": "heading",
      "text": "How a Compass Works"
    },
    {
      "type": "paragraph",
      "text": "Earth behaves like a giant, slightly lopsided bar magnet. A compass needle is a small magnet free to spin, so it aligns itself with that field and the marked end swings to point roughly toward the north magnetic pole. That is all there is to the magic: a lightweight magnet floating in fluid, settling into line with the planet's magnetic field. Keep it level and away from anything metal or electronic, which can pull the needle off course."
    },
    {
      "type": "heading",
      "text": "Magnetic North Is Not True North"
    },
    {
      "type": "paragraph",
      "text": "Here is the catch that trips up beginners. There are two norths. True north is the geographic North Pole, the top of Earth's axis, and it is what map grids are built around. Magnetic north is where your compass needle actually points, and it sits some distance away. Worse, the magnetic pole drifts over time as the planet's molten interior churns."
    },
    {
      "type": "paragraph",
      "text": "The angle between the two, at your particular location, is called declination, or magnetic variation. It might be a few degrees east or west, or much more in some regions, and good maps print the local declination in the margin. To navigate accurately you adjust for it, either by doing the arithmetic or by setting the declination on a compass that allows it. Skip this step on a long journey and you can drift well off your intended line."
    },
    {
      "type": "heading",
      "text": "Taking a Bearing From a Map"
    },
    {
      "type": "paragraph",
      "text": "A bearing is simply a direction expressed as an angle from 0 to 360 degrees, where 0 is north, 90 is east, 180 is south, and 270 is west. To find the bearing from where you are to where you want to go:"
    },
    {
      "type": "list",
      "items": [
        "Lay the edge of the compass baseplate along the line from your current position to your destination on the map, with the direction-of-travel arrow pointing the way you want to go.",
        "Rotate the compass housing until its orienting lines are parallel with the map's north-south grid lines, north on the housing pointing to north on the map.",
        "Read the bearing at the index line. That is your map bearing.",
        "Adjust for declination to turn it into a bearing you can actually walk, unless your compass is already set for local declination."
      ]
    },
    {
      "type": "heading",
      "text": "Following a Bearing on the Ground"
    },
    {
      "type": "paragraph",
      "text": "Now hold the compass flat in front of you and turn your whole body, not just the compass, until the magnetic needle sits inside the orienting arrow, often described as putting the red in the shed. The direction-of-travel arrow now points exactly where you need to go. To stay on line over distance, pick out a distinctive landmark dead ahead, a boulder, a lone tree, a notch in a ridge, walk to it, and then take a fresh sighting. This stops small errors from compounding over a long hike."
    },
    {
      "type": "heading",
      "text": "Orienting the Map Itself"
    },
    {
      "type": "paragraph",
      "text": "A simpler everyday trick is to orient the map so it matches the world. Turn the map until its north lines up with the compass needle, allowing for declination, and suddenly the rivers, roads, and ridgelines on paper point the same way as the real ones in front of you. This alone makes a map far easier to read and is often all you need for casual navigation."
    },
    {
      "type": "heading",
      "text": "Build the Mental Map"
    },
    {
      "type": "paragraph",
      "text": "A compass tells you which way you are facing, but the real skill of navigation is reading the land: matching contour lines to the slopes around you, recognising landforms, and building a picture of where you are from the terrain itself. That sense of place is something you can sharpen anywhere. EarthGuessr is a fun way to train it, dropping you into satellite imagery and challenging you to work out your location from the landscape alone, no GPS allowed."
    }
  ]
};
